import React, { useMemo, useState, useCallback } from "react";
import { FiExternalLink, FiChevronDown, FiChevronUp, FiCopy } from "react-icons/fi";
import "./NewsSourceCards.scss";
import { copyToClipboard } from "../../utils/copyToClipboard";

/**
 * Normalize item to article shape (protocol SourceArticle or legacy Source).
 * Preserves icon (e.g. Logo.dev URL) when present.
 */
const toArticle = (item) => ({
  title: item.title ?? item.tile ?? "",
  source: item.source ?? "",
  date: item.date ?? "",
  link: item.link ?? item.url ?? "",
  brand: item.brand,
  icon: item.icon,
});

const STACK_SIZE = 4;

const NewsSourceCards = ({ articles = [], source_type, grounding_sources = [] }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const articleList = useMemo(
    () => (Array.isArray(articles) ? articles.map(toArticle) : []),
    [articles],
  );

  const hasArticles = articleList.length > 0;
  const hasGrounding = Array.isArray(grounding_sources) && grounding_sources.length > 0;

  const sectionLabel =
    source_type === "serp_api" ? "News" : source_type === "gemini_grounding" ? "Sources" : "News";
  const totalArticles = articleList.length;

  const stackedArticles = useMemo(
    () => (hasArticles ? articleList.slice(0, STACK_SIZE) : []),
    [articleList, hasArticles],
  );

  const toggleExpanded = useCallback(() => {
    setIsExpanded((prev) => !prev);
  }, []);

  if (!hasArticles && !hasGrounding) {
    return null;
  }

  const articleMeta = (article) =>
    [article.source, article.date, article.brand].filter(Boolean).join(" · ");

  const headerLabel =
    totalArticles === 1 ? "1 article" : `${totalArticles} articles`;

  return (
    <div className="news-source-cards" role="region" aria-label={sectionLabel}>
      {hasArticles && (
        <div
          className={`news-source-cards__header${isExpanded ? " news-source-cards__header--expanded" : ""}`}
          role="button"
          tabIndex={0}
          aria-expanded={isExpanded}
          aria-label={`${sectionLabel} sources, ${headerLabel}, ${isExpanded ? "collapse" : "expand"}`}
          onClick={toggleExpanded}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              toggleExpanded();
            }
          }}
        >
          <span className="news-source-cards__header-label">{sectionLabel}</span>
          <span className="news-source-cards__stack" aria-hidden>
            {stackedArticles.map((article, index) => {
              const label = article.source || article.title || "";
              const initial = (label || "?").charAt(0).toUpperCase();
              const hasIcon = article.icon && String(article.icon).startsWith("http");
              return (
                <a
                  key={`stack-${index}`}
                  href={article.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="news-source-cards__stack-icon"
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                  aria-label={label ? `Open article: ${label}` : "Open article"}
                >
                  {hasIcon ? (
                    <>
                      <img
                        src={article.icon}
                        alt={label || "Source"}
                        onError={(e) => {
                          const img = e.target;
                          img.style.display = "none";
                          const fallback = img.nextElementSibling;
                          if (fallback) fallback.style.display = "flex";
                        }}
                      />
                      <span className="news-source-cards__stack-fallback" aria-hidden style={{ display: "none" }}>
                        {initial}
                      </span>
                    </>
                  ) : (
                    <span aria-hidden>{initial}</span>
                  )}
                </a>
              );
            })}
          </span>
          <span className="news-source-cards__header-meta">{headerLabel}</span>
          <span className="news-source-cards__header-chevron" aria-hidden>
            {isExpanded ? <FiChevronUp /> : <FiChevronDown />}
          </span>
        </div>
      )}

      {isExpanded && (hasArticles || hasGrounding) && (
        <div className="news-source-cards__body">
          {hasArticles && (
            <ul className="news-source-cards__list" role="list">
              {articleList.map((article, index) => {
                const avatarLabel = article.source || article.title || "?";
                const avatarInitial = avatarLabel.charAt(0).toUpperCase();
                const hasIcon = article.icon && String(article.icon).startsWith("http");
                const handleCopy = (e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  copyToClipboard(article.link || "");
                };
                return (
                <li key={`article-${index}`} className="news-source-cards__item">
                  <div className="news-source-cards__card">
                    <a
                      href={article.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="news-source-cards__card-main"
                    >
                      <span className="news-source-cards__avatar" aria-hidden>
                        {hasIcon ? (
                          <>
                            <img
                              src={article.icon}
                              alt={avatarLabel}
                              onError={(e) => {
                                const img = e.target;
                                img.style.display = "none";
                                const fallback = img.nextElementSibling;
                                if (fallback) fallback.style.display = "flex";
                              }}
                            />
                            <span className="news-source-cards__avatar-fallback" style={{ display: "none" }}>
                              {avatarInitial}
                            </span>
                          </>
                        ) : (
                          avatarInitial
                        )}
                      </span>
                      <span className="news-source-cards__content">
                        <span className="news-source-cards__title">{article.title}</span>
                        <span className="news-source-cards__meta">{articleMeta(article)}</span>
                      </span>
                    </a>
                    <span className="news-source-cards__actions" aria-hidden>
                      <button
                        type="button"
                        className="news-source-cards__action-btn"
                        onClick={handleCopy}
                        aria-label="Copy link"
                      >
                        <FiCopy />
                      </button>
                      <a
                        href={article.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="news-source-cards__action-link"
                        aria-label="Open in new tab"
                      >
                        <FiExternalLink />
                      </a>
                    </span>
                  </div>
                </li>
                );
              })}
            </ul>
          )}
          {hasGrounding && (
            <>
              <h3 className="news-source-cards__label">Grounding</h3>
              <ul className="news-source-cards__list" role="list">
                {grounding_sources.map((gs, index) => {
                  const handleCopyGrounding = (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    copyToClipboard(gs.url || "");
                  };
                  return (
                  <li key={`grounding-${index}`} className="news-source-cards__item">
                    <div className="news-source-cards__card">
                      <a
                        href={gs.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="news-source-cards__card-main"
                      >
                        <span className="news-source-cards__avatar" aria-hidden>
                          {(gs.title || "?").charAt(0).toUpperCase()}
                        </span>
                        <span className="news-source-cards__content">
                          <span className="news-source-cards__title">{gs.title}</span>
                        </span>
                      </a>
                      <span className="news-source-cards__actions" aria-hidden>
                        <button
                          type="button"
                          className="news-source-cards__action-btn"
                          onClick={handleCopyGrounding}
                          aria-label="Copy link"
                        >
                          <FiCopy />
                        </button>
                        <a
                          href={gs.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="news-source-cards__action-link"
                          aria-label="Open in new tab"
                        >
                          <FiExternalLink />
                        </a>
                      </span>
                    </div>
                  </li>
                  );
                })}
              </ul>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default NewsSourceCards;
